import { createContext, useContext, useEffect, useRef, useState, useCallback } from 'react';
import { api } from '@/lib/api';

const AuthCtx = createContext(null);

const HEARTBEAT_MS = 30 * 1000; // 30s ping — backend thresholds: 5m away, 10m offline

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const beatTimer = useRef(null);

  const bootstrap = useCallback(async () => {
    const token = localStorage.getItem('rh_token');
    if (!token) { setLoading(false); return; }
    try {
      const { data } = await api.get('/auth/me');
      setUser(data);
    } catch {
      localStorage.removeItem('rh_token');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  // Heartbeat loop — active only while a user is logged in.
  useEffect(() => {
    const stop = () => { if (beatTimer.current) { clearInterval(beatTimer.current); beatTimer.current = null; } };
    if (!user) { stop(); return; }
    const beat = () => { api.post('/heartbeat').catch(() => { /* silent — offline sweep handles it */ }); };
    beat(); // immediate first beat
    beatTimer.current = setInterval(beat, HEARTBEAT_MS);
    // Also send a beat whenever the tab regains focus, so short glitches recover fast.
    const onFocus = () => beat();
    const onVis = () => { if (document.visibilityState === 'visible') beat(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVis);
    return () => {
      stop();
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [user]);

  const login = async (email, password) => {
    const { data } = await api.post('/auth/login', { email, password });
    localStorage.setItem('rh_token', data.token);
    setUser(data.user);
    return data.user;
  };

  const logout = () => {
    localStorage.removeItem('rh_token');
    setUser(null);
  };

  return (
    <AuthCtx.Provider value={{ user, setUser, loading, login, logout }}>
      {children}
    </AuthCtx.Provider>
  );
}

export const useAuth = () => useContext(AuthCtx);
